import type { HubConnection } from '@microsoft/signalr';
import {
    createContext,
    type ReactNode,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useReducer,
    useRef,
} from 'react';
import { getCallStackItemData } from '../../../sources/debugger';
import type {
    Breakpoint,
    DebugAction,
    DebugTabId,
    StackItem,
    SwitchConfirmation,
} from '../../../types/debugger';
import type { CriteriaType } from '../../../types/generic';
import { useNotificationDispatch } from '../../notifications/NotificationProvider';
import { useTabs } from '../../tabs/TabsProvider';
import { connection } from './hubConnect';
import { checkBreaks } from './tabs/breakpoints/evaluateBreakpoints';

interface DebuggerState {
    callStack: StackItem[];
    breakpoints: Breakpoint[];
    isDebuggerAttached: boolean;
    switchFlowConfirmation: SwitchConfirmation | null;
    activeTabId: DebugTabId;
    callStackItemsArePinned: boolean;
}

const initialState: DebuggerState = {
    breakpoints: [],
    callStack: [],
    isDebuggerAttached: false,
    activeTabId: 'STATE_VALUES',
    callStackItemsArePinned: false,
    switchFlowConfirmation: null,
};

const DebugStateContext = createContext<DebuggerState | undefined>(undefined);
const DebugDispatchContext = createContext<((action: DebugAction) => void) | undefined>(undefined);

interface DebugProviderProps {
    stateId: string;
    flowId: string;
    startElementId: null | string;
    initialState?: Partial<DebuggerState>;
    children?: ReactNode;
    zoomToMapElement: (elementId: string) => void;
    isActive: boolean;
    onDebuggerAttached?: () => void;
}

// Breakpoints must be re-evaluated against the call stack whenever they're added or changed
const reEvaluateBreakpointHits = (callStack: StackItem[], breakpoints: Breakpoint[]): StackItem[] =>
    callStack.map((callStackItem) =>
        callStackItem.stateValues
            ? {
                  ...callStackItem,
                  breakpointsHit: checkBreaks(callStackItem.stateValues, breakpoints),
              }
            : callStackItem,
    );

const reducer = (state: DebuggerState, action: DebugAction): DebuggerState => {
    switch (action.type) {
        case 'setDebugStateId':
            return {
                ...state,
                switchFlowConfirmation: null,
            };
        case 'dismissSwitchFlowConfirmation':
            return {
                ...state,
                switchFlowConfirmation: null,
            };
        case 'setIsDebuggerAttached':
            return {
                ...state,
                isDebuggerAttached: action.isAttached,
            };
        case 'setActiveTab':
            return {
                ...state,
                activeTabId: action.activeTabId,
            };
        case 'setCallStackItemsPinned':
            return {
                ...state,
                callStackItemsArePinned: action.pinned,
            };
        case 'addToCallStack': {
            const shouldSelect = !state.callStackItemsArePinned;

            // Deselect any current selection
            const deselectedCallStack = shouldSelect
                ? state.callStack.map((item) => ({ ...item, isSelected: false }))
                : state.callStack;

            const newCallStackItem: StackItem = {
                id: action.stackItemId,
                flowId: action.stackItemFlowId,
                flowName: action.flowName,
                mapElementId: action.mapElementId,
                developerName: action.mapElementName,
                dateTimeExecuted: new Date().toLocaleString(undefined, {
                    dateStyle: 'medium',
                    timeStyle: 'medium',
                }),
                stateValues: null,
                rootFaults: null,
                breakpointsHit: null,
                isSelected: shouldSelect,
            };

            return { ...state, callStack: [newCallStackItem, ...deselectedCallStack] };
        }
        case 'setSelectedCallStackItem': {
            const updatedCallStack = state.callStack.map((callStackItem) => ({
                ...callStackItem,
                isSelected: callStackItem.id === action.stackItemId && !callStackItem.isSelected,
            }));
            return { ...state, callStack: updatedCallStack };
        }
        case 'setCallStackItemData': {
            const updatedCallStack = state.callStack.map((callStackItem) => {
                if (callStackItem.id === action.stackItemId) {
                    // Breakpoints must be evaluated as call stack items come through
                    const breakpointsHit = checkBreaks(
                        action.callStackItemData.stateValues,
                        state.breakpoints,
                    );

                    return {
                        ...callStackItem,
                        stateValues: action.callStackItemData.stateValues,
                        rootFaults: action.callStackItemData.rootFaults,
                        breakpointsHit,
                    };
                }

                return callStackItem;
            });

            return { ...state, callStack: updatedCallStack };
        }
        case 'addBreakpoint': {
            const newBreakpoint = action.breakpoint || {
                id: crypto.randomUUID(),
                value: null,
                operator: '' as CriteriaType,
                expectedContentValue: '',
            };

            const breakpoints = [...state.breakpoints, newBreakpoint];
            const callStack = reEvaluateBreakpointHits(state.callStack, [newBreakpoint]);

            return { ...state, breakpoints, callStack };
        }
        case 'updateBreakpoint': {
            const breakpoints = state.breakpoints.map((breakpoint) =>
                breakpoint.id === action.breakpoint.id ? action.breakpoint : breakpoint,
            );
            const callStack = reEvaluateBreakpointHits(state.callStack, breakpoints);

            return { ...state, breakpoints, callStack };
        }
        case 'removeBreakpoint': {
            const filteredBreakpoints = state.breakpoints.filter(
                (breakpoint) => breakpoint.id !== action.breakpointId,
            );
            return {
                ...state,
                breakpoints: filteredBreakpoints,
            };
        }
        case 'switchFlow': {
            return {
                ...state,
                switchFlowConfirmation: action.switchFlowConfirmation,
            };
        }
        case 'clearCallStack':
            return {
                ...state,
                callStack: [],
            };
    }
};

const DebugProvider = ({
    stateId,
    flowId,
    startElementId,
    initialState: suppliedInitialState,
    zoomToMapElement,
    isActive,
    children,
    onDebuggerAttached,
}: DebugProviderProps) => {
    const [debugState, dispatch] = useReducer(reducer, {
        ...initialState,
        ...(suppliedInitialState ?? {}),
    });

    const dispatchNotification = useNotificationDispatch();

    const { tabs } = useTabs();

    const hubConnection = useRef<HubConnection | null>(null);

    const isFlowTabAlreadyOpen = useCallback(
        (idOfFlowToSwitchTo: string) => {
            return tabs.some((tab) => tab.elementId === idOfFlowToSwitchTo);
        },
        [tabs],
    );

    const getStateValues = useCallback(
        async (stackItemId: string) => {
            try {
                const callStackItemData = await getCallStackItemData(stackItemId);
                dispatch({
                    type: 'setCallStackItemData',
                    callStackItemData,
                    stackItemId,
                });
            } catch (error) {
                dispatchNotification({
                    type: 'notifyError',
                    payload: {
                        error,
                    },
                });
            }
        },
        [dispatchNotification],
    );

    const attachDebugger = useCallback(
        async (stateIdToAttach: string) => {
            try {
                await hubConnection.current?.invoke('AttachDebugger', stateIdToAttach);
                dispatch({ type: 'setIsDebuggerAttached', isAttached: true });
                onDebuggerAttached?.();
            } catch (error) {
                dispatchNotification({
                    type: 'notifyError',
                    payload: {
                        error,
                    },
                });
            }
        },
        [dispatchNotification, onDebuggerAttached],
    );

    // biome-ignore lint/correctness/useExhaustiveDependencies: Requires dedicated refactor to add all dependencies
    const addToCallStack = useCallback(
        (
            mapElementId: string,
            stackItemId: string,
            stackItemFlowId: string,
            mapElementName: string,
            flowName: string,
        ) => {
            getStateValues(stackItemId);
            dispatch({
                type: 'addToCallStack',
                mapElementId,
                stackItemId,
                stackItemFlowId,
                mapElementName,
                flowName,
            });
        },
        [],
    );

    const selectedCallStackItem = useMemo(
        () => debugState.callStack.find((callStackItem) => callStackItem.isSelected),
        [debugState.callStack],
    );

    useEffect(() => {
        hubConnection.current = connection();

        hubConnection.current?.on('StackItemReceived', addToCallStack);

        let wasStopped = false;

        const startConnection = async () => {
            try {
                await hubConnection.current?.start();

                await attachDebugger(stateId);

                dispatch({ type: 'setIsDebuggerAttached', isAttached: true });
            } catch (error) {
                // Effect has been cleaned-up
                if (wasStopped) {
                    return;
                }

                dispatchNotification({
                    type: 'notifyError',
                    payload: {
                        error,
                    },
                });
            }
        };

        const stopConnection = async () => {
            try {
                wasStopped = true;
                await hubConnection.current?.stop();
            } catch (error) {
                dispatchNotification({
                    type: 'notifyError',
                    payload: {
                        error,
                    },
                });
            }
        };

        startConnection();

        return () => {
            stopConnection();
        };
    }, [stateId, dispatchNotification, addToCallStack, attachDebugger]);

    useEffect(() => {
        if (!(isActive && selectedCallStackItem && startElementId)) {
            return;
        }

        if (selectedCallStackItem.flowId !== flowId) {
            dispatch({
                type: 'switchFlow',
                switchFlowConfirmation: {
                    stackItemId: selectedCallStackItem.id,
                    stackItemFlowId: selectedCallStackItem.flowId,
                    stackItemFlowName: selectedCallStackItem.flowName,
                    canSwitch: !isFlowTabAlreadyOpen(selectedCallStackItem.flowId),
                },
            });
        } else {
            // Focus the canvas on the map element associated to the currently selected call stack item
            zoomToMapElement(selectedCallStackItem.mapElementId);
        }
    }, [
        // A change to the start element ID indicates a new flow has been rendered
        startElementId,
        flowId,
        isActive,
        selectedCallStackItem,
        isFlowTabAlreadyOpen,
        zoomToMapElement,
    ]);

    return (
        <DebugStateContext.Provider value={debugState}>
            <DebugDispatchContext.Provider value={dispatch}>
                {children}
            </DebugDispatchContext.Provider>
        </DebugStateContext.Provider>
    );
};

const useDebugState = (): DebuggerState => {
    const context = useContext(DebugStateContext);
    if (context === undefined) {
        throw new Error('useDebugState must be used within a DebugProvider');
    }
    return context;
};

const useDebugDispatch = (): ((action: DebugAction) => void) => {
    const context = useContext(DebugDispatchContext);
    if (context === undefined) {
        throw new Error('useDebugDispatch must be used within a DebugProvider');
    }
    return context;
};

export { DebugProvider, useDebugDispatch, useDebugState };
